import React, { useCallback, useEffect, useRef, useState } from "react"
import BasicModal from "../GlobalComponents/BasicModal"
import { IconButton } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import { PlusButton } from "../GlobalComponents/CommonComponents"
import UserRegistrationModal from "./UserRegistrationModal"
import Search from "../GlobalComponents/Search"
import Loader from "../GlobalComponents/Loader"
import UserCards from "./UserCards"
import { showToast } from "../GlobalComponents/Toast"
import { debounce } from "lodash"
import { useDispatch } from "react-redux"
import { setActiveTab } from "../../redux/reducer/appReducer"
import {
  docterTableKeys,
  REGISTER,
  tableInitialDataCount,
} from "../../utils/constants"
import CommonUserCards from "./CommonUserCards"
import { getUsersData } from "../../services/userServices"
import "./index.css"

function UserRegistrationContainer(props) {
  const formFields = {
    name: "",
    password: "",
    email: "",
    role: "",
    profileUrl: "",
    show: false,
  }
  let modalFields = {
    onClose: onCloseModal,
    onSubmit: onSubmit,
    title: "Add New User",
    show: false,
    formData: formFields,
    showColorPicker: true,
    showColorPicker: true,
    isEditing: false,
  }
  // const [formData, setFormData] = useState(formFields);
  const [usersData, setUsersData] = useState([])
  const [isLoading, setIsLoading] = useState([])
  const [loadingStatus, setLoadingStatus] = useState("hide")
  const [modalData, setModalData] = useState(modalFields)
  const [paginationData, setPaginationData] = useState({
    count: 0,
    endReached: false,
    searchValue: "",
  })
  const observerRef = useRef(null)
  const tableContainerRef = useRef(null)
  const tableLimit = tableInitialDataCount
  const dispatch = useDispatch()

  useEffect(() => {
    debouncedFilter.cancel()
    debouncedFilter(paginationData.searchValue)
  }, [paginationData.searchValue])

  useEffect(() => {
    dispatch(setActiveTab(REGISTER))
  }, [])

  // Scroll to top when search value changes
  useEffect(() => {
    if (tableContainerRef.current) {
      // Try to find the TableContainer element within the ref
      const tableContainer = tableContainerRef.current.querySelector('.MuiTableContainer-root')
      if (tableContainer) {
        tableContainer.scrollTo({ top: 0, behavior: 'auto' })
      } else {
        // Fallback to the container div itself
        tableContainerRef.current.scrollTo({ top: 0, behavior: 'auto' })
      }
    }
  }, [paginationData?.searchValue])

  const debouncedFilter = useCallback(
    debounce((searchKey) => {
      setPaginationData({ searchValue: searchKey, count: 1, endReached: false })
      getData(0, searchKey)
    }, 300),
    []
  )

  useEffect(() => {
    if (paginationData?.endReached) {
      setLoadingStatus("hide")
    }
  }, [paginationData?.endReached])

  function handleSearchChange(e) {
    setPaginationData((prev) => ({ ...prev, searchValue: e.target.value }))
  }

  async function getData(offset, searchKey) {
    if (paginationData.endReached) {
      return
    }

    setLoadingStatus(prev => {
      if (prev === "loaded") {
        return "loading"
      }
    })

    try {
      const data = await getUsersData({
        searchKey,
        offset: offset ? offset : paginationData?.count,
        limit: tableLimit,
      })

      if (data.error) {
        showToast("error", data.error)
        setUsersData([])
      } else {
        if (data?.userDetails) {
          if (data?.userDetails.length < tableLimit) {
            setPaginationData((prev) => ({ ...prev, endReached: true }))
          }
          if (offset === 0) {
            setUsersData(data?.userDetails ?? [])
          } else {
            setUsersData((prev) => [...prev, ...data?.userDetails])
          }
        }
      }
    } catch (error) {
      console.log("getData error", error)
    }

    setIsLoading(false)
    setLoadingStatus("loaded")
  }

  function onCloseModal() {
    setModalData((prev) => ({ ...prev, show: false, formData: formFields, isEditing: false }))
  }

  function onSubmit() {
    // setOpenModal(false);
    setModalData((prev) => ({ ...prev, show: false, formData: formFields }))
    setPaginationData({ searchValue: "", count: 1, endReached: false })
    getData(0, "")
    setIsLoading(true)
  }

  //uncomment this to enable infinite scroll

  const callObserver = (entries) => {
    if (entries[0].isIntersecting) {
      setPaginationData((prev) => {
        if (!prev.endReached && prev.count !== 0) {
          getData(prev.count, prev.searchValue)
          return { ...prev, count: prev.count + 1 }
        }
        return { ...prev }
      })
    }
  }
  useEffect(() => {
    const observer = new IntersectionObserver(callObserver, { threshold: 1 })
    if (observerRef.current) {
      observer.observe(observerRef.current)
    }
    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current)
      }
    }
  }, [observerRef, paginationData?.searchValue])

  function handleEdit(selectedData) {
    setModalData((prev) => ({
      ...prev,
      show: true,
      title: "Edit User",
      isEditing: true,
      formData: selectedData,
    }))
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "87%",
        padding: "20px 40px",
      }}
    >
      {isLoading && <Loader />}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        {/* <div style={{ marginRight: "10px", marginLeft: "auto" }}> */}
        <div className="search-container">
          <Search
            addStyle={{ width: "100%" }}
            placeholder="Search by Name or Role"
            searchValue={paginationData.searchValue}
            handleSearchChange={handleSearchChange}
            onClose={() =>
              setPaginationData((prev) => ({ ...prev, searchValue: "" }))
            }
          />
        </div>
        <div style={{ marginLeft: "auto" }}>
          <PlusButton
            onClickPlus={() =>
              setModalData((prev) => ({
                ...prev,
                show: true,
                title: "Add New User",
              }))
            }
          />
        </div>
      </div>
      <CommonUserCards
        usersData={usersData}
        handleEdit={handleEdit}
        isLoading={isLoading}
        searchValue={paginationData.searchValue}
        headerKeys={docterTableKeys}
        isPatientData={false}
        observer={observerRef}
        loadingStatus={loadingStatus}
        tableContainerRef={tableContainerRef}
      />
      {modalData.show && <UserRegistrationModal {...modalData} />}
    </div>
  )
}

export default UserRegistrationContainer
